import {
    DailySummaryAlignmentType,
    DailySummaryBreakdownItemType,
    DailySummaryBreakdownType,
    DailySummaryInsightsType,
    DailySummaryProteinBonusType,
    DailyUserSummaryType,
    SummaryDisplayType,
    SummaryScoreType,
    WeeklySummaryCategoryAverageType,
    WeeklySummaryCategoryAveragesType,
    WeeklySummaryFocusAreaType,
    WeeklySummaryInsightsType,
    WeeklySummaryAlignmentType,
    WeeklySummaryAlignmentOverallType,
    WeeklySummaryAlignmentTrafficLightCountsType,
    WeeklySummaryAlignmentCardType,
    WeeklySummaryAlignmentSuggestedFocusType,
    WeeklySummaryNutritionType,
    WeeklySummaryTrafficLightDistributionType,
    WeeklySummaryTrendType,
    WeeklyUserSummaryType,
    userType,
} from "../../DTO";
import { Api } from "../api";
import { convertToFoodTypeList } from "./food-log/list";

export const getDailyUserSummary = (
    user: userType,
    userId: number,
    dayId: number,
) => {
    return new Promise<{ summary: DailyUserSummaryType }>((resolve, reject) => {
        const api = new Api({
            method: "get",
            path: `summary/daily/user/${userId}/${dayId}`,
            ignore404Redirect: true,
            header: {
                Authorization: `Bearer ${user.access_token}`,
            },
        });

        return api
            .call()
            .then((data: any) => {
                const payload = data?.data?.data ?? data?.data ?? {};
                resolve({
                    summary: convertToDailyUserSummary(payload),
                });
            })
            .catch((err) => reject(err?.data?.message || err?.message || "Not found"));
    });
};

export const getWeeklyUserSummary = (
    user: userType,
    userId: number,
    weekId: number,
) => {
    return new Promise<{ summary: WeeklyUserSummaryType }>(
        (resolve, reject) => {
            const api = new Api({
                method: "get",
                path: `summary/weekly/user/${userId}/${weekId}`,
                ignore404Redirect: true,
                header: {
                    Authorization: `Bearer ${user.access_token}`,
                },
            });

            return api
                .call()
                .then((data: any) => {
                    const payload = data?.data?.data ?? data?.data ?? {};
                    resolve({
                        summary: convertToWeeklyUserSummary(payload),
                    });
                })
                .catch((err) => reject(err?.data?.message || err?.message || "Not found"));
        },
    );
};

export const convertToSummaryDisplay = (data: any): SummaryDisplayType => {
    return {
        title: data?.title ?? "",
        light: data?.light ?? "red",
        color_code: data?.color_code ?? "#EF4444",
        emoji: data?.emoji ?? "",
    };
};

export const convertToSummaryScore = (data: any): SummaryScoreType => {
    return {
        total: Number(data?.total ?? 0),
        max: Number(data?.max ?? 0),
        percentage: Number(data?.percentage ?? 0),
    };
};

export const convertToDailySummaryBreakdownItem = (
    data: any,
): DailySummaryBreakdownItemType => {
    return {
        points: Number(data?.points ?? 0),
        max: Number(data?.max ?? 0),
        light: data?.light ?? "red",
    };
};

export const convertToDailySummaryBreakdown = (
    data: any,
): DailySummaryBreakdownType => {
    return {
        carbs: convertToDailySummaryBreakdownItem(data?.carbs),
        sugar: convertToDailySummaryBreakdownItem(data?.sugar),
        fiber: convertToDailySummaryBreakdownItem(data?.fiber),
        protein: convertToDailySummaryBreakdownItem(data?.protein),
    };
};

export const convertToDailySummaryProteinBonus = (
    data: any,
): DailySummaryProteinBonusType => {
    return {
        earned: Boolean(data?.earned),
        points: Number(data?.points ?? 0),
        high_protein_meals: Number(data?.high_protein_meals ?? 0),
        required_meals: Number(data?.required_meals ?? 0),
    };
};

export const convertToDailySummaryInsights = (
    data: any,
): DailySummaryInsightsType => {
    return {
        worst_category: data?.worst_category ?? "",
        daily_insight: data?.daily_insight ?? "",
        tomorrow_focus: data?.tomorrow_focus ?? "",
    };
};

export const convertToDailyUserSummary = (data: any): DailyUserSummaryType => {
    return {
        id: Number(data?.id ?? 0),
        daily_task_id: Number(data?.daily_task_id ?? 0),
        has_summary: Boolean(data?.has_summary),
        display: convertToSummaryDisplay(data?.display),
        score: convertToSummaryScore(data?.score),
        scores_breakdown: convertToDailySummaryBreakdown(
            data?.scores_breakdown,
        ),
        protein_bonus: convertToDailySummaryProteinBonus(data?.protein_bonus),
        insights: convertToDailySummaryInsights(data?.insights),
        ai_summary: data?.ai_summary ?? "",
        nutrition_totals: data?.nutrition_totals ?? {},
        suggestions: Array.isArray(data?.suggestions)
            ? data.suggestions.map((item: any) => String(item))
            : [],
        generated_at: data?.generated_at ?? "",
        alignment_summary: data?.alignment_summary
            ? convertToDailySummaryAlignment(data?.alignment_summary)
            : null,
        food_logs: Array.isArray(data?.food_logs)
            ? convertToFoodTypeList(data.food_logs)
            : [],
    };
};

export const convertToDailySummaryAlignment = (
    data: any,
): DailySummaryAlignmentType => {
    return {
        day_id: Number(data?.day_id ?? 0),
        overall: convertToWeeklySummaryAlignmentOverall(data?.overall),
        cards: Array.isArray(data?.cards)
            ? data.cards.map(convertToWeeklySummaryAlignmentCard)
            : [],
        suggested_focus: convertToWeeklySummaryAlignmentSuggestedFocus(
            data?.suggested_focus,
        ),
    };
};

export const convertToWeeklySummaryTrafficLightDistribution = (
    data: any,
): WeeklySummaryTrafficLightDistributionType => {
    return {
        green_days: Number(data?.green_days ?? 0),
        amber_days: Number(data?.amber_days ?? 0),
        red_days: Number(data?.red_days ?? 0),
        days_with_data: Number(data?.days_with_data ?? 0),
        total_days: Number(data?.total_days ?? 0),
    };
};

export const convertToWeeklySummaryCategoryAverage = (
    data: any,
): WeeklySummaryCategoryAverageType => {
    return {
        avg_score: Number(data?.avg_score ?? 0),
        light: data?.light ?? "red",
    };
};

export const convertToWeeklySummaryCategoryAverages = (
    data: any,
): WeeklySummaryCategoryAveragesType => {
    return {
        carbs: convertToWeeklySummaryCategoryAverage(data?.carbs),
        sugar: convertToWeeklySummaryCategoryAverage(data?.sugar),
        fiber: convertToWeeklySummaryCategoryAverage(data?.fiber),
        protein: convertToWeeklySummaryCategoryAverage(data?.protein),
    };
};

export const convertToWeeklySummaryTrend = (
    data: any,
): WeeklySummaryTrendType => {
    return {
        direction: data?.direction ?? "",
        icon: data?.icon ?? "",
        score_change: Number(data?.score_change ?? 0),
        description: data?.description ?? "",
    };
};

export const convertToWeeklySummaryInsights = (
    data: any,
): WeeklySummaryInsightsType => {
    return {
        weekly_insight: data?.weekly_insight ?? "",
        worst_category: data?.worst_category ?? "",
        focus_areas: Array.isArray(data?.focus_areas)
            ? data.focus_areas.map(convertToWeeklySummaryFocusArea)
            : [],
    };
};

export const convertToWeeklySummaryFocusArea = (
    data: any,
): WeeklySummaryFocusAreaType => {
    return {
        category: data?.category ?? "",
        recommendation: data?.recommendation ?? "",
    };
};

export const convertToWeeklySummaryNutrition = (
    data: any,
): WeeklySummaryNutritionType => {
    return {
        totals: data?.totals ?? {},
        daily_averages: data?.daily_averages ?? {},
    };
};

export const convertToWeeklySummaryAlignmentTrafficLightCounts = (
    data: any,
): WeeklySummaryAlignmentTrafficLightCountsType => {
    return {
        green: Number(data?.green ?? 0),
        amber: Number(data?.amber ?? 0),
        red: Number(data?.red ?? 0),
        no_data: Number(data?.no_data ?? 0),
    };
};

export const convertToWeeklySummaryAlignmentOverall = (
    data: any,
): WeeklySummaryAlignmentOverallType => {
    return {
        traffic_light_counts: convertToWeeklySummaryAlignmentTrafficLightCounts(
            data?.traffic_light_counts,
        ),
        overall_status: data?.overall_status ?? "red",
    };
};

export const convertToWeeklySummaryAlignmentCard = (
    data: any,
): WeeklySummaryAlignmentCardType => {
    return {
        key: data?.key ?? "",
        title: data?.title ?? "",
        score: Number(data?.score ?? 0),
        status: data?.status ?? "red",
        reason: data?.reason ?? "",
        metrics: data?.metrics ?? {},
        data_points: data?.data_points ?? {},
    };
};

export const convertToWeeklySummaryAlignmentSuggestedFocus = (
    data: any,
): WeeklySummaryAlignmentSuggestedFocusType => {
    return {
        key: data?.key ?? "",
        title: data?.title ?? "",
        message: data?.message ?? "",
        recommended_action: data?.recommended_action ?? "",
    };
};

export const convertToWeeklySummaryAlignment = (
    data: any,
): WeeklySummaryAlignmentType => {
    return {
        week_id: Number(data?.week_id ?? 0),
        overall: convertToWeeklySummaryAlignmentOverall(data?.overall),
        cards: Array.isArray(data?.cards)
            ? data.cards.map(convertToWeeklySummaryAlignmentCard)
            : [],
        suggested_focus: convertToWeeklySummaryAlignmentSuggestedFocus(
            data?.suggested_focus,
        ),
    };
};

export const convertToWeeklyUserSummary = (
    data: any,
): WeeklyUserSummaryType => {
    return {
        id: Number(data?.id ?? 0),
        week_id: Number(data?.week_id ?? 0),
        has_summary: Boolean(data?.has_summary),
        display: convertToSummaryDisplay(data?.display),
        score: convertToSummaryScore(data?.score),
        traffic_light_distribution:
            convertToWeeklySummaryTrafficLightDistribution(
                data?.traffic_light_distribution,
            ),
        category_averages: convertToWeeklySummaryCategoryAverages(
            data?.category_averages,
        ),
        trend: convertToWeeklySummaryTrend(data?.trend),
        insights: convertToWeeklySummaryInsights(data?.insights),
        nutrition: convertToWeeklySummaryNutrition(data?.nutrition),
        generated_at: data?.generated_at ?? "",
        alignment_summary: data?.alignment_summary
            ? convertToWeeklySummaryAlignment(data?.alignment_summary)
            : null,
    };
};
